import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Play, Pause, Share2, ArrowLeft, Download } from 'lucide-react';
import { audioApi, getAudioFileUrl } from '@/lib/api';
import { AudioVisualBadge } from '@/components/audio/AudioVisuals';
import { AUDIO_COLORS } from '@/lib/constants';
import { shareAudio } from '@/lib/share';

const AudioPublicPage = () => {
  const { audioId } = useParams();
  const navigate = useNavigate();
  const [audio, setAudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [shared, setShared] = useState(false);
  const [usefulCount, setUsefulCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [showLoginHint, setShowLoginHint] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    loadAudio();
  }, [audioId]);

  const loadAudio = async () => {
    try {
      const data = await audioApi.getAudio(audioId);
      setAudio(data);
      setUsefulCount(data.useful_count || 0);
      // Auto-play después de cargar
      setTimeout(() => setIsPlaying(true), 500);
    } catch (err) {
      setError('Audio no encontrado');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (audioRef.current && audio) {
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, audio]);

  const handleShare = async () => {
    if (!audio) return;
    const baseUrl = window.location.origin;
    const result = await shareAudio(audio, baseUrl);
    if (result.success) {
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    }
  };

  const handleUseful = async () => {
    if (!audio || liked) return;
    try {
      await audioApi.markUseful(audio.audio_id);
      setUsefulCount(prev => prev + 1);
      setLiked(true);
    } catch (err) {
      if (err.response?.status === 401) {
        setShowLoginHint(true);
      }
    }
  };

  const colorData = audio ? (AUDIO_COLORS[audio.color] || AUDIO_COLORS.blue) : AUDIO_COLORS.blue;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !audio) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-6xl mb-4">🔇</p>
          <h2 className="text-xl font-bold text-white mb-2">Audio no encontrado</h2>
          <p className="text-white/60 mb-6">Este audio ya no está disponible</p>
          <Link 
            to="/"
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full inline-block"
          >
            Explorar AudioToo
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header simple */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <span className="text-sm">🎙️</span>
            </div>
            <span className="text-lg font-bold text-white">AudioToo</span>
          </Link>
          <Link 
            to="/"
            className="text-white/60 text-sm hover:text-white"
          >
            Explorar más
          </Link>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        {/* Card del audio */}
        <div 
          className="rounded-3xl p-8 text-center backdrop-blur-xl border border-white/20"
          style={{ background: `linear-gradient(135deg, ${colorData.hex}30 0%, ${colorData.hex}10 100%)` }}
        >
          {/* Badge grande: forma del tipo de audio + color de temática */}
          <div className="flex justify-center mb-6">
            <AudioVisualBadge
              shape={audio.shape}
              color={audio.color}
              emoji={audio.emoji}
              language={audio.language}
              size="lg"
            />
          </div>

          {/* Info */}
          <div className="mb-6">
            <p className="text-white/60 text-sm mb-1">
              {colorData.emoji} {colorData.name}
            </p>
            <p className="text-white font-medium">
              @{audio.username}
            </p>
            <p className="text-white/50 text-sm">
              {audio.duration_seconds} segundos
            </p>
          </div>

          {/* Player */}
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 transition-all ${
              isPlaying 
                ? 'bg-green-500 shadow-[0_0_40px_rgba(34,197,94,0.4)]' 
                : 'bg-gradient-to-r from-purple-500 to-pink-500 shadow-xl'
            }`}
          >
            {isPlaying ? (
              <Pause size={36} className="text-white" />
            ) : (
              <Play size={36} className="text-white ml-1" />
            )}
          </button>

          <audio
            ref={audioRef}
            src={getAudioFileUrl(audio.audio_url)}
            onEnded={() => setIsPlaying(false)}
          />

          {/* Botón ECO - Compartir */}
          <button
            onClick={handleShare}
            className={`w-full py-4 rounded-2xl font-medium flex items-center justify-center gap-3 transition-all ${
              shared 
                ? 'bg-green-500 text-white' 
                : 'bg-white/10 backdrop-blur text-white hover:bg-white/20'
            }`}
          >
            <Share2 size={20} />
            {shared ? '¡Copiado!' : 'ECO - Compartir'}
          </button>

          {/* Botón Útil */}
          <button
            onClick={handleUseful}
            disabled={liked}
            className={`w-full mt-3 py-4 rounded-2xl font-medium flex items-center justify-center gap-3 transition-all ${
              liked
                ? 'bg-green-500 text-white'
                : 'bg-white/10 backdrop-blur text-white hover:bg-white/20'
            }`}
          >
            👍 Útil ({usefulCount})
          </button>
          {showLoginHint && !liked && (
            <p className="text-white/50 text-xs mt-2">Inicia sesión para marcar como útil</p>
          )}
        </div>

        {/* CTA para explorar más */}
        <div className="mt-8 text-center">
          <p className="text-white/50 text-sm mb-4">
            Descubre miles de audios en AudioToo
          </p>
          <Link 
            to="/"
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-full inline-block font-medium"
          >
            Explorar AudioToo
          </Link>
        </div>

        {/* Info de la app */}
        <div className="mt-12 bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
          <h3 className="text-white font-bold mb-2">🎧 ¿Qué es AudioToo?</h3>
          <p className="text-white/60 text-sm">
            El hub de audios cortos. Memes, clips de podcast, consejos, música... 
            Todo en 30 segundos. Sube, escucha y comparte.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AudioPublicPage;
